import React from 'react';
import { FormGroup, Label, Input, CardBody } from 'reactstrap';
import { Size, SizeOptionsRequest } from '~/lib/ApiModel';
import {
    getBackupCost,
    getMaxInterfacesForUser,
    getOffsiteBackupsCost,
    SizeOptionsWithDefaultsApplied,
} from '~/lib/helper/SizeHelper';
import { numberOfBackups } from '~/lib/helper/BackupHelper';
import { observer } from 'mobx-react';
import ErrorList from '../ErrorList';
import IpAddressOption from './IpAddressOption';
import BackupOptions from './BackupOptions';
import OffsiteOptions from './OffsiteOption';
import { formatCurrency } from '~/lib/helper/StringHelper';

export type SizeOptionsEditorProps = {
    showAdvanced: boolean;
    sizeSelections: SizeOptionsWithDefaultsApplied;
    size: Size;
    onSizeSelectionsChange: (selections_update: Partial<SizeOptionsRequest>) => unknown;
    errors: { [K in keyof SizeOptionsWithDefaultsApplied]?: string[] };
};

@observer
export default class SizeOptionsEditor extends React.Component<SizeOptionsEditorProps> {
    backupFieldBasic() {
        const {
            sizeSelections,
            onSizeSelectionsChange,
            size: { options: sizeOptions },
            errors,
        } = this.props;
        const backupErrors = (errors.daily_backups ?? [])
            .concat(errors.weekly_backups ?? [])
            .concat(errors.monthly_backups ?? [])
            .concat(errors.offsite_backups ?? []);

        const changeSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
            const selection = e.target.value;
            if (selection === 'daily') {
                onSizeSelectionsChange({
                    daily_backups: 2,
                    weekly_backups: 0,
                    monthly_backups: 0,
                    offsite_backups: false,
                });
            }

            if (selection === 'daily-offsite') {
                onSizeSelectionsChange({
                    daily_backups: 2,
                    weekly_backups: 0,
                    monthly_backups: 0,
                    offsite_backups: true,
                });
            }

            if (selection === 'none') {
                onSizeSelectionsChange({
                    daily_backups: 0,
                    weekly_backups: 0,
                    monthly_backups: 0,
                    offsite_backups: false,
                });
            }
        };

        const elements = (
            <FormGroup tag="fieldset">
                <Label>Backups</Label>
                <FormGroup check>
                    <Label check>
                        <Input
                            type="radio"
                            name="backupSelection"
                            value="daily"
                            onChange={changeSelection}
                            checked={sizeSelections.daily_backups === 2 && !sizeSelections.offsite_backups}
                        />
                        &nbsp;Onsite daily backups, stored for 2 days (+
                        {formatCurrency(getBackupCost(sizeSelections.disk, 2, sizeOptions.daily_backups, sizeOptions))})
                    </Label>
                </FormGroup>
                <FormGroup check>
                    <Label check>
                        <Input
                            type="radio"
                            name="backupSelection"
                            value="daily-offsite"
                            onChange={changeSelection}
                            checked={sizeSelections.daily_backups === 2 && sizeSelections.offsite_backups}
                        />
                        &nbsp;Onsite and offsite daily backups, stored for 2 days (+
                        {formatCurrency(
                            getBackupCost(sizeSelections.disk, 2, sizeOptions.daily_backups, sizeOptions) +
                                getOffsiteBackupsCost(
                                    {
                                        ...sizeSelections,
                                        daily_backups: 2,
                                        weekly_backups: 0,
                                        monthly_backups: 0,
                                        offsite_backups: true,
                                    },
                                    sizeOptions
                                )
                        )}
                        )
                    </Label>
                </FormGroup>
                <FormGroup check>
                    <Label check>
                        <Input
                            type="radio"
                            name="backupSelection"
                            value="none"
                            onChange={changeSelection}
                            checked={sizeSelections.daily_backups === 0}
                        />
                        &nbsp;Backups are not required
                    </Label>
                </FormGroup>
                <ErrorList errors={backupErrors} />
            </FormGroup>
        );

        return <CardBody className="sizeOptions">{elements}</CardBody>;
    }

    renderIpAddressSelector() {
        const { sizeSelections, onSizeSelectionsChange, size, errors = {} } = this.props;
        return (
            <IpAddressOption
                interfaces={sizeSelections.ipv4_addresses}
                costPerInterface={size.options.ipv4_addresses_cost_per_address}
                discountForNone={size.options.discount_for_no_public_ipv4}
                onChange={(num) => onSizeSelectionsChange({ ipv4_addresses: num })}
                maxInterfaces={getMaxInterfacesForUser(size)}
                errors={errors.ipv4_addresses}
            />
        );
    }

    renderOffsiteBackupOption() {
        const { sizeSelections, onSizeSelectionsChange, errors = {} } = this.props;
        const offsiteCost = getOffsiteBackupsCost(
            { ...sizeSelections, offsite_backups: true },
            this.props.size.options
        );
        const hasBackups = numberOfBackups(sizeSelections) > 0;

        return (
            <OffsiteOptions
                enabled={!!sizeSelections.offsite_backups}
                onChange={(enabled) => onSizeSelectionsChange({ offsite_backups: enabled })}
                errors={errors.offsite_backups}
                monthlyCost={offsiteCost}
                disabled={!hasBackups}
            />
        );
    }

    renderBackupSelections() {
        const { sizeSelections, onSizeSelectionsChange, size, errors = {} } = this.props;
        return (
            <BackupOptions
                costPerBackup={size.options.backups_cost_per_backup_per_gigabyte * sizeSelections.disk}
                inclusions={size.options}
                selections={sizeSelections}
                errors={errors}
                onChange={(partial) => onSizeSelectionsChange(partial)}
            />
        );
    }

    render() {
        const advancedFields = (
            <>
                <CardBody className="sizeOptions">
                    <p>
                        Additional IP addresses may be purchased for approved uses including multiple SSL certificates.
                    </p>
                    {this.renderIpAddressSelector()}
                </CardBody>
                <CardBody className="sizeOptions">
                    <p>Automatic on-site backups are available daily, weekly, monthly, or any combination thereof.</p>
                    {this.renderBackupSelections()}
                </CardBody>
                <CardBody className="sizeOptions">
                    <p>For additional protection, your on-site backups can be duplicated to an off-site location.</p>
                    {this.renderOffsiteBackupOption()}
                </CardBody>
            </>
        );

        return this.props.showAdvanced ? advancedFields : this.backupFieldBasic();
    }
}
